import { supabase } from '@/integrations/supabase/client'
import { smartleadAPI } from '@/lib/smartlead-api'
import type {
  DiscoveryResult,
  MailboxAnalysis,
  SmartleadAnalysis,
  ComparisonItem,
  Discrepancy,
  StatusComparison,
  DiscrepancyReport,
  SyncPlan,
  MailboxCreation,
  MailboxUpdate,
  MailboxDeactivation,
  MailboxReactivation,
  ConflictResolution,
  SyncRecommendation,
  FieldChange
} from './smartlead-discovery-types'

export class SmartleadDiscoveryService {
  private startTime: number = 0
  private apiCallsCount: number = 0
  private errors: string[] = []

  async discoverMailboxes(): Promise<DiscoveryResult> {
    this.startTime = Date.now()
    this.apiCallsCount = 0
    this.errors = []

    console.log('🔍 Starting Smartlead Discovery Service...')

    try {
      // Phase 1: Fetch database mailboxes
      console.log('📊 Fetching database mailboxes...')
      const databaseMailboxes = await this.fetchDatabaseMailboxes()

      // Phase 2: Fetch Smartlead mailboxes
      console.log('🔄 Fetching Smartlead mailboxes...')
      const smartleadMailboxes = await this.fetchSmartleadMailboxes()

      // Phase 3: Compare and analyze
      console.log('🔍 Comparing and analyzing data...')
      const comparison = await this.compareMailboxes(databaseMailboxes, smartleadMailboxes)

      // Phase 4: Generate discrepancy report
      console.log('📋 Generating discrepancy report...')
      const discrepancies = this.createDiscrepancyReport(comparison)

      // Phase 5: Create sync plan
      console.log('📝 Creating sync plan...')
      const syncPlan = this.generateSyncPlan(comparison)

      const result: DiscoveryResult = {
        summary: {
          totalInDatabase: databaseMailboxes.length,
          totalInSmartlead: smartleadMailboxes.length,
          successfullyMatched: comparison.inBoth.length,
          databaseOnly: comparison.onlyInDatabase.length,
          smartleadOnly: comparison.onlyInSmartlead.length,
          statusDiscrepancies: comparison.statusMismatches.length,
          lastDatabaseSync: this.getLastDatabaseSync(databaseMailboxes),
          lastSmartleadFetch: new Date().toISOString()
        },
        databaseMailboxes,
        smartleadMailboxes,
        comparison,
        discrepancies,
        syncPlan,
        metadata: {
          discoveryStarted: new Date(this.startTime).toISOString(),
          discoveryCompleted: new Date().toISOString(),
          processingTimeMs: Date.now() - this.startTime,
          apiCallsMade: this.apiCallsCount,
          errors: this.errors
        }
      }

      console.log('✅ Discovery completed successfully')
      return result

    } catch (error) {
      console.error('❌ Discovery failed:', error)
      this.errors.push(`Discovery failed: ${error}`)
      throw error
    }
  }

  private async fetchDatabaseMailboxes(): Promise<MailboxAnalysis[]> {
    try {
      // Paginate to bypass Supabase's default 1000 row limit
      const allData: any[] = []
      const pageSize = 1000
      let offset = 0

      while (true) {
        const { data, error } = await supabase
          .from('mailboxes')
          .select(`
            id,
            email,
            domain,
            from_name,
            provider,
            status,
            api_sync_status,
            smartlead_id,
            warmup_status,
            warmup_reputation,
            message_per_day,
            daily_limit,
            current_daily_sent,
            last_api_synced_at,
            sync_attempt_count,
            api_sync_error,
            created_at,
            updated_at
          `)
          .order('email')
          .range(offset, offset + pageSize - 1)

        if (error) {
          throw new Error(`Database fetch failed: ${error.message}`)
        }

        if (!data || data.length === 0) break
        allData.push(...data)
        if (data.length < pageSize) break
        offset += pageSize
      }

      console.log(`📊 Fetched ${allData.length} mailboxes from database (paginated)`)
      return allData.map(this.mapDatabaseMailbox)
    } catch (error) {
      console.error('❌ Failed to fetch database mailboxes:', error)
      this.errors.push(`Database fetch error: ${error}`)
      throw error
    }
  }

  private async fetchSmartleadMailboxes(): Promise<SmartleadAnalysis[]> {
    try {
      console.log('🔄 Fetching ALL Smartlead mailboxes with pagination...')
      
      const allMailboxes = []
      let offset = 0
      const limit = 100
      let batchCount = 0
      
      while (true) {
        batchCount++
        console.log(`📧 Fetching Smartlead batch ${batchCount} (offset: ${offset}, limit: ${limit})...`)
        
        this.apiCallsCount++
        const batch = await this.fetchSmartleadBatch(offset, limit)
        
        if (!batch || batch.length === 0) {
          console.log(`✅ No more mailboxes found. Finished at batch ${batchCount - 1}`)
          break
        }
        
        allMailboxes.push(...batch)
        console.log(`📊 Batch ${batchCount}: Added ${batch.length} mailboxes. Total so far: ${allMailboxes.length}`)
        
        // If we got fewer results than the limit, we've reached the last page
        if (batch.length < limit) {
          console.log(`✅ Reached last page (got ${batch.length} < ${limit}). Total mailboxes: ${allMailboxes.length}`)
          break
        }
        
        offset += limit
      }
      
      console.log(`🔄 Fetched ${allMailboxes.length} total mailboxes from Smartlead`)
      return allMailboxes.map(this.mapSmartleadMailbox)
    } catch (error) {
      console.error('❌ Failed to fetch Smartlead mailboxes:', error)
      this.errors.push(`Smartlead fetch error: ${error}`)
      throw error
    }
  }

  private async fetchSmartleadBatch(offset: number, limit: number): Promise<any[]> {
    try {
      const apiKey = this.getSmartleadApiKey()
      const url = `https://server.smartlead.ai/api/v1/email-accounts?offset=${offset}&limit=${limit}&api_key=${apiKey}`
      
      console.log(`🔗 Fetching: ${url.replace(apiKey, '***HIDDEN***')}`)
      
      const response = await fetch(url)
      
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          console.error('🔑 API Key authentication failed during mailbox fetch')
          throw new Error('Invalid API key - please check your Smartlead API key')
        }
        console.error(`❌ Smartlead API failed: ${response.status} ${response.statusText}`)
        const errorText = await response.text()
        console.error(`❌ Error details: ${errorText}`)
        throw new Error(`API request failed: ${response.status}`)
      }
      
      const batch = await response.json()
      console.log(`📦 Received batch: ${batch.length} mailboxes`)
      
      return batch
    } catch (error) {
      console.error(`❌ Failed to fetch batch (offset: ${offset}, limit: ${limit}):`, error)
      throw error
    }
  }

  private getSmartleadApiKey(): string {
    // Get API key from localStorage (same as the existing system)
    const apiKey = localStorage.getItem('SMARTLEAD_API_KEY')
    if (!apiKey) {
      throw new Error('Smartlead API key not found in localStorage')
    }
    return apiKey
  }

  private mapDatabaseMailbox(data: any): MailboxAnalysis {
    return {
      id: data.id,
      email: data.email,
      domain: data.domain,
      from_name: data.from_name,
      provider: data.provider,
      status: data.status,
      api_sync_status: data.api_sync_status,
      smartlead_id: data.smartlead_id,
      warmup_status: data.warmup_status,
      warmup_reputation: data.warmup_reputation,
      message_per_day: data.message_per_day,
      daily_limit: data.daily_limit,
      current_daily_sent: data.current_daily_sent,
      last_api_synced_at: data.last_api_synced_at,
      sync_attempt_count: data.sync_attempt_count,
      api_sync_error: data.api_sync_error,
      created_at: data.created_at,
      updated_at: data.updated_at
    }
  }

  private mapSmartleadMailbox(data: any): SmartleadAnalysis {
    return {
      id: data.id || data.smartlead_id,
      email: data.email || data.from_email,
      from_name: data.from_name || data.name,
      provider: data.provider || 'Unknown',
      status: data.status || 'Unknown',
      warmup_enabled: data.warmup_enabled || false,
      warmup_reputation: data.warmup_reputation || 0,
      daily_limit: data.daily_limit || 0,
      current_daily_sent: data.current_daily_sent || 0,
      total_sent: data.total_sent || 0,
      total_replied: data.total_replied || 0,
      total_bounced: data.total_bounced || 0,
      reply_rate: data.reply_rate || 0,
      bounce_rate: data.bounce_rate || 0,
      last_activity: data.last_activity || null,
      created_at: data.created_at,
      updated_at: data.updated_at
    }
  }

  private async compareMailboxes(
    databaseMailboxes: MailboxAnalysis[],
    smartleadMailboxes: SmartleadAnalysis[]
  ): Promise<{
    inBoth: ComparisonItem[]
    onlyInDatabase: ComparisonItem[]
    onlyInSmartlead: ComparisonItem[]
    statusMismatches: ComparisonItem[]
  }> {
    const databaseMap = new Map(databaseMailboxes.map(m => [m.email.toLowerCase(), m]))
    const smartleadMap = new Map(smartleadMailboxes.map(m => [m.email.toLowerCase(), m]))

    const inBoth: ComparisonItem[] = []
    const onlyInDatabase: ComparisonItem[] = []
    const onlyInSmartlead: ComparisonItem[] = []
    const statusMismatches: ComparisonItem[] = []

    // Find mailboxes in both systems
    for (const [email, dbMailbox] of databaseMap) {
      const smartleadMailbox = smartleadMap.get(email)
      
      if (smartleadMailbox) {
        const comparison = this.createComparisonItem(dbMailbox, smartleadMailbox)
        inBoth.push(comparison)
        
        // Check for status mismatches
        if (this.hasStatusMismatch(dbMailbox, smartleadMailbox)) {
          statusMismatches.push(comparison)
        }
      } else {
        onlyInDatabase.push({
          email: dbMailbox.email,
          databaseData: dbMailbox,
          smartleadData: null,
          discrepancies: [],
          matchType: 'database_only'
        })
      }
    }

    // Find mailboxes only in Smartlead
    for (const [email, smartleadMailbox] of smartleadMap) {
      if (!databaseMap.has(email)) {
        onlyInSmartlead.push({
          email: smartleadMailbox.email,
          databaseData: null,
          smartleadData: smartleadMailbox,
          discrepancies: [],
          matchType: 'smartlead_only'
        })
      }
    }

    return {
      inBoth,
      onlyInDatabase,
      onlyInSmartlead,
      statusMismatches
    }
  }

  private createComparisonItem(
    dbMailbox: MailboxAnalysis,
    smartleadMailbox: SmartleadAnalysis
  ): ComparisonItem {
    const discrepancies = this.findDiscrepancies(dbMailbox, smartleadMailbox)
    
    let matchType: ComparisonItem['matchType'] = 'perfect'
    if (discrepancies.length === 0) {
      matchType = 'perfect'
    } else if (discrepancies.some(d => d.severity === 'critical' || d.severity === 'high')) {
      matchType = 'conflict'
    } else {
      matchType = 'partial'
    }

    return {
      email: dbMailbox.email,
      databaseData: dbMailbox,
      smartleadData: smartleadMailbox,
      discrepancies,
      matchType
    }
  }

  private findDiscrepancies(
    dbMailbox: MailboxAnalysis,
    smartleadMailbox: SmartleadAnalysis
  ): Discrepancy[] {
    const discrepancies: Discrepancy[] = []

    // Compare status
    if (dbMailbox.status !== smartleadMailbox.status) {
      discrepancies.push({
        field: 'status',
        databaseValue: dbMailbox.status,
        smartleadValue: smartleadMailbox.status,
        severity: 'high',
        description: `Status mismatch: DB has '${dbMailbox.status}', Smartlead has '${smartleadMailbox.status}'`,
        recommendation: 'Review status in both systems and align'
      })
    }

    // Compare warmup status
    const dbWarmup = dbMailbox.warmup_status === 'ACTIVE'
    if (dbWarmup !== smartleadMailbox.warmup_enabled) {
      discrepancies.push({
        field: 'warmup_status',
        databaseValue: dbWarmup,
        smartleadValue: smartleadMailbox.warmup_enabled,
        severity: 'medium',
        description: `Warmup status mismatch: DB warmup is ${dbWarmup}, Smartlead warmup is ${smartleadMailbox.warmup_enabled}`,
        recommendation: 'Sync warmup status between systems'
      })
    }

    // Compare reputation
    const dbReputation = parseInt(dbMailbox.warmup_reputation || '0')
    if (Math.abs(dbReputation - smartleadMailbox.warmup_reputation) > 5) {
      discrepancies.push({
        field: 'warmup_reputation',
        databaseValue: dbReputation,
        smartleadValue: smartleadMailbox.warmup_reputation,
        severity: 'medium',
        description: `Reputation mismatch: DB has ${dbReputation}%, Smartlead has ${smartleadMailbox.warmup_reputation}%`,
        recommendation: 'Update reputation from Smartlead (source of truth)'
      })
    }

    // Compare daily limits
    if (dbMailbox.daily_limit !== smartleadMailbox.daily_limit) {
      discrepancies.push({
        field: 'daily_limit',
        databaseValue: dbMailbox.daily_limit,
        smartleadValue: smartleadMailbox.daily_limit,
        severity: 'low',
        description: `Daily limit mismatch: DB has ${dbMailbox.daily_limit}, Smartlead has ${smartleadMailbox.daily_limit}`,
        recommendation: 'Sync daily limits between systems'
      })
    }

    return discrepancies
  }

  private hasStatusMismatch(
    dbMailbox: MailboxAnalysis,
    smartleadMailbox: SmartleadAnalysis
  ): boolean {
    return dbMailbox.status !== smartleadMailbox.status ||
           (dbMailbox.warmup_status === 'ACTIVE') !== smartleadMailbox.warmup_enabled
  }

  private createDiscrepancyReport(comparison: any): DiscrepancyReport {
    const allDiscrepancies: Discrepancy[] = []
    
    comparison.inBoth.forEach((item: ComparisonItem) => {
      allDiscrepancies.push(...item.discrepancies)
    })

    const severityCounts = allDiscrepancies.reduce((acc, d) => {
      acc[d.severity] = (acc[d.severity] || 0) + 1
      return acc
    }, {} as Record<string, number>)

    const fieldCounts = allDiscrepancies.reduce((acc, d) => {
      acc[d.field] = (acc[d.field] || 0) + 1
      return acc
    }, {} as Record<string, number>)

    const topIssues = Object.entries(fieldCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5)
      .map(([field, count]) => ({
        field,
        count,
        examples: allDiscrepancies
          .filter(d => d.field === field)
          .slice(0, 3)
          .map(d => d.description)
      }))

    return {
      totalDiscrepancies: allDiscrepancies.length,
      criticalDiscrepancies: severityCounts.critical || 0,
      highDiscrepancies: severityCounts.high || 0,
      mediumDiscrepancies: severityCounts.medium || 0,
      lowDiscrepancies: severityCounts.low || 0,
      discrepancies: allDiscrepancies,
      topIssues
    }
  }

  private generateSyncPlan(comparison: any): SyncPlan {
    const create: MailboxCreation[] = []
    const update: MailboxUpdate[] = []
    const deactivate: MailboxDeactivation[] = []
    const reactivate: MailboxReactivation[] = []
    const noAction: string[] = []
    const conflicts: ConflictResolution[] = []
    const recommendations: SyncRecommendation[] = []

    // Process mailboxes only in Smartlead (create in database)
    comparison.onlyInSmartlead.forEach((item: ComparisonItem) => {
      if (item.smartleadData) {
        create.push({
          email: item.email,
          smartleadData: item.smartleadData,
          reason: 'Mailbox exists in Smartlead but not in database',
          priority: 'high'
        })
      }
    })

    // Process mailboxes only in database (deactivate or create in Smartlead)
    comparison.onlyInDatabase.forEach((item: ComparisonItem) => {
      if (item.databaseData) {
        deactivate.push({
          email: item.email,
          databaseId: item.databaseData.id,
          reason: 'Mailbox exists in database but not in Smartlead',
          smartleadStatus: 'Not found in Smartlead'
        })
      }
    })

    // Process status mismatches
    comparison.statusMismatches.forEach((item: ComparisonItem) => {
      if (item.databaseData && item.smartleadData) {
        const changes: FieldChange[] = []
        
        item.discrepancies.forEach(discrepancy => {
          changes.push({
            field: discrepancy.field,
            oldValue: discrepancy.databaseValue,
            newValue: discrepancy.smartleadValue,
            changeType: 'update'
          })
        })

        if (changes.length > 0) {
          update.push({
            email: item.email,
            databaseId: item.databaseData.id,
            changes,
            reason: 'Status and settings mismatch with Smartlead',
            priority: 'high'
          })
        }
      }
    })

    // Generate recommendations
    if (create.length > 0) {
      recommendations.push({
        type: 'create',
        email: `${create.length} mailboxes`,
        reason: 'New mailboxes found in Smartlead',
        priority: 'high',
        estimatedImpact: 'Will add new mailboxes to database'
      })
    }

    if (update.length > 0) {
      recommendations.push({
        type: 'update',
        email: `${update.length} mailboxes`,
        reason: 'Status mismatches need resolution',
        priority: 'high',
        estimatedImpact: 'Will update mailbox statuses and settings'
      })
    }

    const riskLevel = this.calculateRiskLevel(create, update, deactivate)
    const estimatedTime = this.estimateSyncTime(create, update, deactivate)

    return {
      actions: {
        create,
        update,
        deactivate,
        reactivate,
        noAction
      },
      conflicts,
      recommendations,
      estimatedTime,
      riskLevel
    }
  }

  private calculateRiskLevel(
    create: MailboxCreation[],
    update: MailboxUpdate[],
    deactivate: MailboxDeactivation[]
  ): 'low' | 'medium' | 'high' {
    const totalActions = create.length + update.length + deactivate.length
    
    if (totalActions === 0) return 'low'
    if (totalActions < 10) return 'medium'
    return 'high'
  }

  private estimateSyncTime(
    create: MailboxCreation[],
    update: MailboxUpdate[],
    deactivate: MailboxDeactivation[]
  ): number {
    // Rough estimates: 2s per create, 1s per update, 0.5s per deactivate
    return (create.length * 2000) + (update.length * 1000) + (deactivate.length * 500)
  }

  private getLastDatabaseSync(mailboxes: MailboxAnalysis[]): string | null {
    const syncTimes = mailboxes
      .map(m => m.last_api_synced_at)
      .filter(Boolean)
      .sort()
      .reverse()
    
    return syncTimes[0] || null
  }

  // Public utility methods
  async getStatusComparison(): Promise<StatusComparison[]> {
    const result = await this.discoverMailboxes()
    return result.comparison.inBoth.map(item => ({
      email: item.email,
      databaseStatus: item.databaseData?.status || 'Unknown',
      smartleadStatus: item.smartleadData?.status || 'Unknown',
      statusMatch: item.databaseData?.status === item.smartleadData?.status,
      warmupStatusMatch: (item.databaseData?.warmup_status === 'ACTIVE') === item.smartleadData?.warmup_enabled,
      reputationMatch: Math.abs(
        parseInt(item.databaseData?.warmup_reputation || '0') - 
        (item.smartleadData?.warmup_reputation || 0)
      ) <= 5,
      dailyLimitMatch: item.databaseData?.daily_limit === item.smartleadData?.daily_limit,
      overallMatch: item.matchType === 'perfect'
    }))
  }

  async getDiscrepancyReport(): Promise<DiscrepancyReport> {
    const result = await this.discoverMailboxes()
    return result.discrepancies
  }

  async getSyncPlan(): Promise<SyncPlan> {
    const result = await this.discoverMailboxes()
    return result.syncPlan
  }
}
